import { memo, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdAdMediumDto } from '@/services/ws-broadcast/src/main/resources/rest-api';
import type { CampaignPositionType } from '@/services/ws-orders/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { useBroadcastAdMediumOptions } from '../hooks/useBroadcastAdMediumOptions';

interface BroadcastAdMediumFilterProps {
  campaignType: CampaignPositionType.TV | CampaignPositionType.RADIO;
  value: Option<IdAdMediumDto> | null;
  onChange: (value: Option<IdAdMediumDto> | null) => void;
  label?: string;
  dataTestId?: string;
}

export const BroadcastAdMediumFilter: FC<BroadcastAdMediumFilterProps> = memo(function BroadcastAdMediumFilter({
  campaignType,
  value,
  onChange,
  label,
  dataTestId,
}) {
  const [t] = useTranslation(['common']);
  const { options, pending: loading, total, searchFilter, setSearchFilter } = useBroadcastAdMediumOptions({ campaignType });

  const handleChange = useCallbackRef((newValue: Option<IdAdMediumDto> | null) => {
    onChange(newValue);
  });

  const serverSideFiltering = useMemo(
    () => ({ filter: searchFilter, setFilter: setSearchFilter, total }),
    [searchFilter, setSearchFilter, total],
  );

  return (
    <Filter<IdAdMediumDto>
      label={label || t('common:AD_MEDIUM')}
      loading={loading}
      selected={value}
      onChange={handleChange}
      serverSideFiltering={serverSideFiltering}
      options={options}
      dataTestId={dataTestId || 'broadcast-ad-medium-dropdown'}
    />
  );
});
